import {
    FetchState,
    InvestmentThreshold,
    InvestmentCustodian,
    FeatureFlags,
    InvestmentCustodianDisplayName
} from "@/shared/types";
import * as bankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useHsabInvestmentStore } from "@/shared/store/hsabInvestment";
import { useDrivewealthAccountMigrationStore } from "@/shared/store/drivewealthAccountMigration";
import { templatedFetchKeyStore } from "./fetchKeyedPlugin";
import { useDrivewealthStore } from "@/shared/store/drivewealth";


const investmentCustodianDisplayNames: InvestmentCustodianDisplayName[] = [
  ({key: InvestmentCustodian.Drivewealth, displayName: "DriveWealth"}),
  ({key: InvestmentCustodian.Tda, displayName: "TD Ameritrade"}),
  ({key: InvestmentCustodian.Umb, displayName: "UMB"}),
  ({key: InvestmentCustodian.None, displayName: "No Custodian"}),
];

const emptyThresholds = (): InvestmentThreshold => ({
    accountCreate: 0,
    moneyTransfer: 0,
    investmentProvider: InvestmentCustodian.None
});

const useSubStore = defineStore("investmentThreshold_fetchKeyed", () => {
    return templatedFetchKeyStore<InvestmentThreshold>(
        emptyThresholds,
        null,
        bankService.getBalanceThresholds,
        undefined,
        true // retry on 404
    );
});

export const useInvestmentThresholdStore = defineStore("investmentThreshold", {
    getters: {
        thresholdsLoaded (): boolean {
            return useSubStore().fetchState === FetchState.Loaded &&
                   useCustodianSettingsStore().isLoaded &&
                   useFeatureFlagStore().featureFlagsAvailable &&
                   !this.isWaitingOnHsabInvestmentService;
        },
        isDrivewealthInvestmentProvider (): boolean {
            return this.investmentProvider === InvestmentCustodian.Drivewealth;
        },
        isHsabInvestmentProvider (): boolean {
            return this.investmentProvider === InvestmentCustodian.HsaBank;
        },
        thresholdForAccountCreation (): number {
            const hsabInvestmentStore = useHsabInvestmentStore();
            const drivewealthAccountMigrationStore = useDrivewealthAccountMigrationStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            const subStore = useSubStore();
            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;
            const hasLegacyInvestmentAccount: boolean = hsabInvestmentStore.hasLegacyInvestmentAccount;


            const disableInvestmentThreshold: boolean = (
                subStore.investmentProvider === InvestmentCustodian.Drivewealth &&
                isHsaBankCustodian &&
                drivewealthAccountMigrationStore.accountMigrationActive &&
                hasLegacyInvestmentAccount
            );

            if (disableInvestmentThreshold) {
                return 0;
            }

            return subStore.accountCreate;
        },
        thresholdForMoneyTransfer (): number {
            const subStore = useSubStore();

            return subStore.moneyTransfer;
        },
        investmentThreshold () {
            return (alreadyHasAccount?: boolean | any) => alreadyHasAccount
                ? this.thresholdForMoneyTransfer
                : this.thresholdForAccountCreation;
        },
        investmentProvider (): InvestmentCustodian {
            if (!(useSubStore().fetchState === FetchState.Loaded &&
                useCustodianSettingsStore().isLoaded &&
                useFeatureFlagStore().featureFlagsAvailable)) {
                return InvestmentCustodian.None;
            }

            const subStore = useSubStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            const featureFlagStore = useFeatureFlagStore();
            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;

            if (isHsaBankCustodian &&
                subStore.investmentProvider === InvestmentCustodian.Drivewealth &&
                !featureFlagStore.flags.bend15244DisplayDrivewealthInvestmentForHSABUsers) {
                    return InvestmentCustodian.HsaBank;
            }

            return subStore.investmentProvider;
        },
        investmentProviderName (): string {
            const subStore = useSubStore();

            const pair = investmentCustodianDisplayNames.find(x => x.key === subStore.investmentProvider);
            if (pair) {
              return pair.displayName;
            }
            return `No display name for ${subStore.investmentProvider}`;
        },
        displayInvestmentsSection (): boolean {
            const subStore = useSubStore();
            const drivewealth = useDrivewealthStore();

            return subStore.investmentProvider === InvestmentCustodian.Umb
                || (this.isDrivewealthInvestmentProvider && drivewealth.showInvestments);
        },
        displayLegacyHsabInvestmentsRedirectLink (): boolean {
            const hsabInvestmentStore = useHsabInvestmentStore();

            if (this.isHsabInvestmentProvider) {
                return hsabInvestmentStore.hsabInvestmentsEnabled;
            }

            return false;
        },
        displayMigrationPeriodHsabInvestmentsRedirectLink (): boolean {
            const custodianSettingsStore = useCustodianSettingsStore();
            const hsabInvestmentStore = useHsabInvestmentStore();

            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;

            return isHsaBankCustodian &&
                   this.isDrivewealthInvestmentProvider &&
                   hsabInvestmentStore.hsabInvestmentsEnabled;
        },
        isWaitingOnHsabInvestmentService (): boolean {
            const subStore = useSubStore();

            return useCustodianSettingsStore().isHsaBankCustodian
                && (!useHsabInvestmentStore().investmentAccountStatusLoaded)
                && (subStore.investmentProvider === InvestmentCustodian.HsaBank
                    // HSAB users with DW need to fetch WCF result due to
                    // the migration period
                    ||  subStore.investmentProvider === InvestmentCustodian.Drivewealth);
        },
        shouldLoadHsabInvestmentAccountStatus (): boolean {
            const custodianSettingsStore = useCustodianSettingsStore();
            const isHsaBankCustodian: boolean = custodianSettingsStore.isHsaBankCustodian;

            return isHsaBankCustodian && (this.isDrivewealthInvestmentProvider || this.isHsabInvestmentProvider);
        }
    },
    actions: {
        async loadInvestmentThresholds (options?: { username?: string, partnerId?: number }): Promise<any> {
            const username = options && options.username ? options.username : null;
            const partnerId = options && options.partnerId ? options.partnerId : null;

            const custodianStore = useCustodianSettingsStore();
            const custodianPromise = custodianStore.getCustodianSettings({ username, partnerId } as any);

            const subStore = useSubStore();
            const investmentThresholdPromise = subStore.fetchKeyedApiCall(options);

            return Promise.all([
                custodianPromise,
                investmentThresholdPromise
            ])
            .then(() => {
                if (this.shouldLoadHsabInvestmentAccountStatus) {
                    const hsabInvestmentStore = useHsabInvestmentStore();
                    return hsabInvestmentStore.loadInvestmentAccountStatus({ username, partnerId });
                }
            })
            .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});
